import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Popover, List, Empty, Spin } from "antd";
import { Bell, CheckCheck } from "lucide-react";
import dayjs from "dayjs";
import { authUrl } from "../../../constants/apiBaseUrl";
import { useFetch } from "../../../hooks/useFetch";
import { usePatch } from "../../../hooks/usePatch";
import Auth from "../../../helpers/auth.service";

/**
 * NotificationBell
 *
 * Live in-app notification bell. Unread badge + dropdown list backed by the
 * REST endpoints, with a server-sent-events stream for live pushes (the badge
 * updates without a page refresh). Clicking a GRN notification deep-links to
 * that GRN and marks it read.
 */
export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const esRef = useRef(null);
  const reconnectRef = useRef(null);

  const { loading, runQuery: fetchList } = useFetch({
    url: authUrl.notification_list,
    skip: true,
    onCompleted: (res) => {
      const payload = res?.data?.data;
      if (payload) {
        setItems(payload.notifications || []);
        setUnread(payload.unreadCount ?? 0);
      }
    },
  });

  const { runQuery: countQuery } = useFetch({
    url: authUrl.notification_unread_count,
    skip: true,
    onCompleted: (res) => {
      const payload = res?.data?.data;
      if (payload) setUnread(payload.unreadCount ?? 0);
    },
  });

  const { runQuery: markRead } = usePatch({ url: authUrl.notification_read });

  // Initial unread-count probe.
  useEffect(() => {
    countQuery(authUrl.notification_unread_count, { currentPage: 1, pageSize: 20 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live SSE stream with auto-reconnect.
  const connect = useCallback(() => {
    const token = Auth.token;
    if (!token) return;
    try {
      const es = new EventSource(
        `${authUrl.notification_stream}?token=${encodeURIComponent(token)}`,
      );
      esRef.current = es;
      es.addEventListener("notification", (evt) => {
        try {
          const data = JSON.parse(evt.data);
          setItems((prev) => [data, ...prev].slice(0, 50));
          setUnread((c) => c + 1);
        } catch {
          /* ignore malformed frame */
        }
      });
      es.onerror = () => {
        es.close();
        esRef.current = null;
        // Reconnect after a short delay (EventSource fires onerror on drop).
        if (!reconnectRef.current) {
          reconnectRef.current = setTimeout(() => {
            reconnectRef.current = null;
            connect();
          }, 5000);
        }
      };
    } catch {
      /* EventSource unsupported / construction failed — degrade to REST only */
    }
  }, []);

  useEffect(() => {
    connect();
    return () => {
      if (esRef.current) esRef.current.close();
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
    };
  }, [connect]);

  const handleOpenChange = (next) => {
    setOpen(next);
    if (next) {
      fetchList(authUrl.notification_list, { currentPage: 1, pageSize: 20 });
    }
  };

  const handleMarkAll = async () => {
    await markRead(authUrl.notification_read, {});
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
  };

  const handleClick = async (n) => {
    if (!n.read) {
      await markRead(authUrl.notification_read, { ids: [n.id] });
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((c) => Math.max(0, c - 1));
    }
    setOpen(false);
    if (n.refType === "GRN" && n.refId) {
      navigate(`/grn/${n.refId}`);
    } else if (n.refType === "TICKET" && n.refId) {
      navigate(`/tickets?ticketId=${n.refId}`);
    }
  };

  const content = (
    <div className="w-80 max-w-[90vw]">
      <div className="flex items-center justify-between px-1 pb-2 border-b border-gray-100">
        <span className="font-semibold text-sm text-cocoa">Notifications</span>
        {unread > 0 && (
          <button
            onClick={handleMarkAll}
            className="flex items-center gap-1 text-xs text-terracotta hover:underline"
          >
            <CheckCheck className="h-3.5 w-3.5" /> Mark all read
          </button>
        )}
      </div>
      <div className="max-h-96 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-8">
            <Spin />
          </div>
        ) : items.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No notifications"
            className="py-6"
          />
        ) : (
          <List
            size="small"
            dataSource={items}
            renderItem={(n) => (
              <List.Item
                onClick={() => handleClick(n)}
                className={`cursor-pointer rounded px-2 transition-colors hover:bg-cream ${
                  n.read ? "opacity-70" : "bg-cream/40"
                }`}
              >
                <div className="w-full">
                  <div className="flex items-start gap-2">
                    {!n.read && (
                      <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-terracotta" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-cocoa truncate">{n.title}</div>
                      {n.body && <div className="text-xs text-gray-600">{n.body}</div>}
                      {n.createdAt && (
                        <div className="text-[11px] text-gray-400 mt-0.5">
                          {dayjs(n.createdAt).format("DD/MM/YYYY hh:mm A")}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </List.Item>
            )}
          />
        )}
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      placement="bottomRight"
    >
      <button
        className="relative text-mahogany hover:text-cocoa transition-colors"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 bg-terracotta text-white text-[10px] rounded-full h-4 min-w-4 px-1 flex items-center justify-center">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
    </Popover>
  );
}
